import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, UserPlus, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";

// FonderX Logo
const LOGO_URL = "https://customer-assets.emergentagent.com/job_forex-compare-2/artifacts/l16i8o9b_FONDER%20CURRENCY%20EXCHANGE-4.png";

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { isAuthenticated } = useAuth();

  return (
    <footer className="bg-[#000a1a] border-t border-white/10" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="block mb-5">
              <img 
                src={LOGO_URL} 
                alt="FonderX Currency Exchange" 
                className="h-10 sm:h-12 w-auto"
              />
            </Link>
            <p className="text-blue-100/50 text-sm leading-relaxed mb-6">
              Trusted currency exchange services in New Westminster. Competitive rates on fiat and cryptocurrency.
            </p>
            {/* Sign Up CTA */}
            {!isAuthenticated && (
              <Link 
                to="/auth?mode=register" 
                className="inline-flex items-center gap-2 bg-[#3B82F6] hover:bg-[#2563EB] text-white px-5 py-2.5 rounded-lg transition-colors text-sm font-medium"
                data-testid="footer-signup-btn"
              >
                <UserPlus className="w-4 h-4" />
                Create Account
              </Link>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Pages</h4>
            <ul className="space-y-3">
              <li><Link to="/" className="text-blue-100/50 hover:text-white transition-colors text-sm">Home</Link></li>
              <li><Link to="/about" className="text-blue-100/50 hover:text-white transition-colors text-sm">About Us</Link></li>
              <li><Link to="/heroes" className="text-blue-100/50 hover:text-white transition-colors text-sm">Frontline Heroes Rate</Link></li>
              <li><Link to="/crypto" className="text-blue-100/50 hover:text-white transition-colors text-sm">Crypto Prices</Link></li>
              <li><Link to="/blog" className="text-blue-100/50 hover:text-white transition-colors text-sm">Blog</Link></li>
              <li><Link to="/contact" className="text-blue-100/50 hover:text-white transition-colors text-sm">Contact</Link></li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Services</h4>
            <ul className="space-y-3 text-blue-100/50 text-sm">
              <li>Currency Exchange</li>
              <li>Cryptocurrency Exchange</li>
              <li>Business Services</li>
              <li>
                <Link to="/crypto/academy" className="hover:text-white transition-colors">
                  FonderX Academy
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-wider">Contact Us</h4>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#3B82F6] flex-shrink-0 mt-0.5" />
                <span className="text-blue-100/50 text-sm">2-723 12th Street, New Westminster, BC</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />
                <a href="tel:+17787590400" className="text-blue-100/50 hover:text-white text-sm transition-colors">+1 (778) 759-0400</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />
                <a href="mailto:info@fonderx.com" className="text-blue-100/50 hover:text-white text-sm transition-colors">info@fonderx.com</a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-[#3B82F6] flex-shrink-0" />
                <span className="text-blue-100/50 text-sm">Mon-Fri: 9:30AM - 6PM</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="mt-10 sm:mt-12 pt-6 sm:pt-8 border-t border-white/10">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-blue-100/40 text-xs sm:text-sm text-center sm:text-left">
              &copy; {currentYear} FonderX Currency Exchange Inc. All rights reserved.
            </p>
            <div className="flex items-center gap-4 text-xs sm:text-sm">
              <Link to="/terms-of-service" className="text-blue-100/40 hover:text-white transition-colors">Terms of Service</Link>
              <span className="text-blue-100/20">|</span>
              <Link to="/privacy-policy" className="text-blue-100/40 hover:text-white transition-colors">Privacy Policy</Link>
              <span className="text-blue-100/20">|</span>
              <span className="text-blue-100/40">FINTRAC Registered MSB #N300001076</span>
            </div>
          </div>
          <p className="mt-5 text-blue-100/30 text-[11px] text-center" data-testid="footer-rate-disclaimer">
            Exchange rates are subject to change without notice.
          </p>
          {/* Developer Credit */}
          <div className="mt-6 text-center">
            <a 
              href="https://www.averoan.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-100/30 hover:text-blue-400 text-xs transition-colors"
            >
              Powered by Averoan Technologies Inc.
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
